import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import Modal, { Field, TextInput, TextArea, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const initial = { site_id: "", name: "", description: "", status: "planning", progress: 0, cover_url: "", expected_completion: "" };

export default function Projects() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/company/projects"), api.get("/company/sites")]);
    setItems(p.data);
    setSites(s.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/projects", { ...form, progress: Number(form.progress) });
      toast.success("Project created");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-projects-page">
      <PageHeader
        eyebrow="Portfolio"
        title="Projects"
        description="Your residential and commercial developments."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-project-button">+ New Project</PrimaryButton>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="grid-surface overflow-hidden" data-testid={`project-${p.id}`}>
            <div className="relative h-44 bg-stone-200">
              {p.cover_url && <img alt="" src={p.cover_url} className="w-full h-full object-cover" />}
              <div className="absolute top-3 left-3"><StatusBadge value={p.status} /></div>
              <div className="absolute bottom-3 right-3 bg-white/95 px-2 py-1 font-mono text-xs font-bold">{p.progress}%</div>
            </div>
            <div className="p-5">
              <div className="overline text-stone-500">{p.site?.name || "No site"}</div>
              <h3 className="font-display text-xl font-bold mt-1">{p.name}</h3>
              <p className="text-sm text-stone-600 mt-2 line-clamp-2">{p.description}</p>
              <div className="mt-4 grid grid-cols-3 border-t border-stone-200 pt-3 text-xs font-mono gap-2">
                <div><div className="text-stone-400">TOWERS</div><div className="font-bold text-base text-stone-950">{p.tower_count ?? 0}</div></div>
                <div><div className="text-stone-400">UNITS</div><div className="font-bold text-base text-stone-950">{p.unit_count ?? 0}</div></div>
                <div><div className="text-stone-400">EXP COMP</div><div className="font-bold text-xs text-stone-950">{fmtDate(p.expected_completion)}</div></div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="grid-surface border-dashed p-10 text-center overline text-stone-400 col-span-full">
            No projects yet
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New project">
        <form onSubmit={submit}>
          <Field label="Site">
            <Select testId="form-proj-site" required value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
              <option value="">Select site…</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Name"><TextInput testId="form-proj-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><TextArea testId="form-proj-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select testId="form-proj-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="planning">Planning</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on_hold">On Hold</option>
              </Select>
            </Field>
            <Field label="Progress %"><TextInput testId="form-proj-progress" type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} /></Field>
          </div>
          <Field label="Cover image URL"><TextInput testId="form-proj-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></Field>
          <Field label="Expected completion"><TextInput testId="form-proj-completion" type="date" value={form.expected_completion} onChange={(e) => setForm({ ...form, expected_completion: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-project">Create</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
